<template>
  <div>
    <v-card class="my-1">
      <v-card-title>Monthly Payroll Summary</v-card-title>
      <v-row class="px-4">
        <v-col cols="10">
          <v-row>
            <v-col>
              <v-autocomplete
                v-model="selectedPayrollPeriod"
                v-validate="'required'"
                :loading="dropdownLoading"
                label="Payroll Period"
                :item-text="item => this.$moment(item.payroll_date).format('YYYY-MM')"
                item-value="id"
                :items="payrollPeriods"
                clearable
                hide-selected
                :error-messages="errors.collect('selectedPayrollPeriod')"
                data-vv-name="selectedPayrollPeriod"  
                data-vv-as="payroll period"
                return-object
              />
            </v-col>

            <v-col>
              <v-autocomplete
                v-model="selectedDynamicColumns"
                :loading="dropdownLoading"
                label="Extra Columns"
                item-text="name"
                item-value="id"
                :items="dynamicColumns"
                clearable
                multiple
                hide-selected 
                chips
                small-chips
                deletable-chips
                :error-messages="errors.collect('selectedDynamicColumns')"
                data-vv-name="selectedDynamicColumns"  
                data-vv-as="extra columns"
              />
            </v-col>
          </v-row>
   
          <v-row>
            <v-col>
              <v-autocomplete
                v-model="selectedCompanies"
                v-validate="'required'"
                :loading="dropdownLoading"
                label="Companies"
                item-text="name"
                item-value="id"
                :items="companies"
                clearable
                multiple
                hide-selected 
                chips
                small-chips
                deletable-chips
                :error-messages="errors.collect('selectedCompanies')"
                data-vv-name="selectedCompanies"  
                data-vv-as="companies"
              />
            </v-col> 

            <v-col>
              <v-autocomplete
                v-model="payrollCycle"
                v-validate="'required'"
                :loading="dropdownLoading"
                label="Payroll Cycles"
                item-text="payroll_cycle.name"
                item-value="payroll_cycle.id"
                :items="selectedPayrollPeriod ? selectedPayrollPeriod.payroll_cycle_transactions : []"
                clearable
                multiple
                hide-selected 
                chips
                small-chips
                deletable-chips
                :error-messages="errors.collect('payrollCycle')"
                data-vv-name="payrollCycle"  
                data-vv-as="payroll cycles"
              />
            </v-col>
          </v-row>  
          <v-row>
            <v-col>
              <v-checkbox v-model="includeSubTotal">
                <template v-slot:label>
                  <div class="font-weight-bold">
                    Include Subtotal in Report
                  </div>
                </template>
              </v-checkbox>
            </v-col>
          </v-row>    
        </v-col>
  
        <v-col
          cols="2"
          class="ma-auto"
        >
          <v-row
            v-if="loading" 
            class="text-center"
          >
            <v-col cols="12">
              <div>
                <span class="font-weight-bold">Downloading</span>
              </div>
              <v-progress-circular
                :rotate="360"
                :size="50"
                :width="5"
                :value="progressbar"
                color="teal"
              >
                {{ progressbar }}
              </v-progress-circular>
            </v-col>
          </v-row>
      
          <v-row class="text-center">
            <v-col cols="12">
              <v-btn
                class="mx-3"
                color="primary"
                :loading="loading"
                @click="generateMonthlyPayrollSummary"
              >
                generate
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card>
  </div>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    data () {
      return {
        loading: false,
        companies: [],
        selectedCompanies: [],
        payrollCycle: null,
        dropdownLoading: false,
        progressbar: 0,
        selectedPayrollPeriod: null,
        payrollPeriods: [],
        dynamicColumns: [
          { id: 'COST CENTER', name: 'COST CENTER' },
          { id: 'DEPARTMENT', name: 'DEPARTMENT' },
          { id: 'POSITION', name: 'POSITION' },
          { id: 'BASED LOCATION (STATE)', name: 'BASED LOCATION (STATE)' },
          { id: 'JOINED DATE', name: 'JOINED DATE' },
          { id: 'RESIGNED DATE', name: 'RESIGNED DATE' },
        ],
        selectedDynamicColumns: [],
        includeSubTotal: true,
      }
    },
    created () {
      this.dropdownLoading = true

      Promise.all([
        this.$api.hrisCompanies().list(),
        this.$api.payrollPeriods().list(),
      ])
        .then(([tempCompanies, tempPayrollPeriods]) => {
          this.companies = _.orderBy(tempCompanies.data, ['name'], ['asc'])

          this.companies.unshift({ id: 'ALL', name: 'All Companies' })

          this.payrollPeriods = _.orderBy(tempPayrollPeriods.data, ['payroll_date'], ['desc'])
        })

      this.dropdownLoading = false
    },
    methods: {
      generateMonthlyPayrollSummary () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.loading = true

            this.$api.payrollReports().monthlyPayrollSummary(this.prepareFormData(), {
              onDownloadProgress: (progressEvent) => {
                this.progressbar = (Math.floor((progressEvent.loaded * 100) / progressEvent.total))
              },
            })
              .then(res => {
                let blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
                let link = document.createElement('a')
                link.href = window.URL.createObjectURL(blob)

                link.setAttribute('download', 'MonthlyPayrollSummaryReport - ' + this.$moment(this.selectedPayrollPeriod.payroll_date).format('MMM YYYY') + ' [' + this.$moment().format('YYYY-MM-DD h.mma') + '].xlsx') 
                link.click()
                this.loading = false
                this.progressbar = 0

                this.$snack({ message: 'Report downloaded successfully' })
              })
              .catch(error => {
                this.loading = false
                if (error.response.status === 400) {
                  this.$snack({ 
                    color: 'error',
                    message: error.response.data.status, 
                  })
                }
              })
          })
      },

      prepareFormData () {
        return {
          payrollPeriodId: this.selectedPayrollPeriod.id,
          dynamicColumns: this.selectedDynamicColumns,
          includeSubTotal: this.includeSubTotal,
          payrollCycles: this.payrollCycle,
          companies: this.selectedCompanies.includes('ALL') ? (
            this.companies.filter(company => {
              return company.id !== 'ALL'
            }).map(company => {
              return company.id
            })
          ) : this.selectedCompanies,
        }
      },
    },
  }
</script>
